import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceTemplate,
  ISupportingActor,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import { Button, IconButton, Typography } from '@material-ui/core';
import { Edit, UserPlus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ButtonAsPlainText } from '../../../../admin/components/ButtonAsPlainText';
import { ChipWithAction } from '../../../../components/ChipWithAction';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { useCurrentUser } from '../../../../services/currentUser';
import {
  AddActorDialog,
  EditActorDialog
} from '../../pages/TemplateEdit/StepTimeline';
import { ActorDescriptor, useActorDescriptorsFromTemplate } from '../Actors';
import { StandardDrawer } from '../StandardDrawer';

const Section = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: p.theme.spacing(1),
  marginBottom: p.theme.spacing(2)
}));

export const ManageActorDrawer = ({
  tpl,
  open,
  onAdd,
  onEdit,
  onRemove,
  onArchive,
  onUnarchive,
  onClose
}: {
  tpl: Doc<IExperienceTemplate>;
  open: boolean;
  onClose: () => void;
  onAdd: (nextActors: ISupportingActor) => void;
  onEdit: (nextActors: ISupportingActor, editedActor: string) => void;
  onRemove: (removedActor: string) => void;
  onArchive: (archivedActor: string) => void;
  onUnarchive: (unarchivedActor: string) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const addActorDialog = useDialogState();
  const [editActorDialogSelection, setEditActorDialogSelection] = useState<
    string | null
  >(null);
  const [showArchived, setShowArchived] = useState(false);
  const activeActorDescriptors = useActorDescriptorsFromTemplate(
    tpl.data.supportingActors
  );
  const allActorDescriptors = useActorDescriptorsFromTemplate(
    tpl.data.supportingActors,
    true,
    true
  );
  const activeActors = useMemo(
    () =>
      Object.values(activeActorDescriptors).filter(
        (a) => a.key !== PARTICIPANT_ACTOR_KEY
      ),
    [activeActorDescriptors]
  );
  const archivedActors = useMemo(
    () =>
      Object.values(allActorDescriptors).filter(
        (a) => a.key !== PARTICIPANT_ACTOR_KEY && !activeActorDescriptors[a.key]
      ),
    [allActorDescriptors, activeActorDescriptors]
  );

  return (
    <>
      <StandardDrawer open={open} onClose={onClose}>
        <Section>
          <FlexContainer justifyContent="space-between" fullWidth>
            <Typography variant="h5" component="h5">
              {t('Manage supporting actors')}
            </Typography>
            <IconButton onClick={onClose}>
              <X size={16} />
            </IconButton>
          </FlexContainer>
        </Section>
        <Section>
          <Typography>
            {t(
              `Supporting actors are people inside the experience besides the participant who can be assigned tasks and receive reminders.`
            )}
          </Typography>
          <Typography>
            {t(
              `Supporting actors should be imported into Dado as users and can either be referenced in each participant's profile data or set to a specific user.`
            )}
          </Typography>
        </Section>
        <Section>
          {!!activeActors.length && (
            <>
              <Typography variant="h6" color="textSecondary">
                {t('Actors')}
              </Typography>
              <ManageActors
                actors={activeActors}
                onClick={setEditActorDialogSelection}
              />
            </>
          )}
          <Button
            size="small"
            color="primary"
            startIcon={<UserPlus size={14} />}
            onClick={addActorDialog.openDialog}
          >
            {t('Add supporting actor')}
          </Button>
        </Section>
        {!!archivedActors.length && (
          <Section>
            <ButtonAsPlainText
              size="small"
              variant="text"
              onClick={() => setShowArchived((s) => !s)}
            >
              {showArchived
                ? t('Hide archived ({{ count }})', {
                    count: archivedActors.length
                  })
                : t('Show archived ({{ count }})', {
                    count: archivedActors.length
                  })}
            </ButtonAsPlainText>
            {showArchived && (
              <ManageActors
                actors={archivedActors}
                onClick={setEditActorDialogSelection}
              />
            )}
          </Section>
        )}
      </StandardDrawer>
      {addActorDialog.dialogOpen && (
        <AddActorDialog
          open={true}
          onClose={addActorDialog.closeDialog}
          actors={Object.values(tpl.data.supportingActors)}
          customFields={org.customUserFields}
          onAdd={onAdd}
          onUnarchive={onUnarchive}
          orgId={tpl.data.orgId}
          tpl={tpl}
        />
      )}
      {editActorDialogSelection && (
        <EditActorDialog
          open={true}
          onClose={() => setEditActorDialogSelection(null)}
          actors={Object.values(tpl.data.supportingActors)}
          customFields={org.customUserFields}
          value={tpl.data.supportingActors[editActorDialogSelection]}
          onChange={(nextActor) => onEdit(nextActor, editActorDialogSelection)}
          onRemove={() => onRemove(editActorDialogSelection)}
          onArchive={() => onArchive(editActorDialogSelection)}
          onUnarchive={() => onUnarchive(editActorDialogSelection)}
          orgId={tpl.data.orgId}
          tpl={tpl}
        />
      )}
    </>
  );
};

const ManageActors = ({
  actors,
  onClick
}: {
  actors: ActorDescriptor[];
  onClick: (key: string) => void;
}) => {
  return (
    <FlexContainer wrap="wrap">
      {actors.map((a) => (
        <ChipWithAction
          key={a.key}
          label={a.label}
          backgroundColor={a.color}
          color="white"
          onClick={() => {
            onClick(a.key);
          }}
          icon={<Edit color="white" size={16} />}
        />
      ))}
    </FlexContainer>
  );
};

export const TemplateManageActorsButton = ({
  tpl,
  onEdit,
  onAdd,
  onRemove,
  onArchive,
  onUnarchive
}: {
  tpl: Doc<IExperienceTemplate>;
  onEdit: (nextActors: ISupportingActor, editedActor: string) => void;
  onAdd: (nextActors: ISupportingActor) => void;
  onRemove: (removedActor: string) => void;
  onArchive: (archivedActor: string) => void;
  onUnarchive: (unarchivedActor: string) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const {
    dialogOpen: drawerOpen,
    openDialog: openDrawer,
    closeDialog: closeDrawer
  } = useDialogState();
  return (
    <>
      <ButtonAsPlainText size="small" variant="text" onClick={openDrawer}>
        {t('Manage')}
      </ButtonAsPlainText>
      <ManageActorDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        tpl={tpl}
        onEdit={onEdit}
        onAdd={onAdd}
        onRemove={onRemove}
        onArchive={onArchive}
        onUnarchive={onUnarchive}
      />
    </>
  );
};
